import React, { useEffect, useMemo, useState } from 'react';
import {
  Sun, Cloud, CloudSun, CloudRain, CloudDrizzle, CloudFog, CloudSnow, CloudLightning,
  Wind, Droplets, Gauge, Sunrise, Sunset, Umbrella, Thermometer, Eye, RefreshCw, Waves,
} from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { setSeoMetadata } from '../../lib/seo';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ICONS = {
  sun: Sun,
  'cloud-sun': CloudSun,
  cloud: Cloud,
  fog: CloudFog,
  drizzle: CloudDrizzle,
  rain: CloudRain,
  showers: CloudRain,
  snow: CloudSnow,
  storm: CloudLightning,
};

const WeatherIcon = ({ icon, className }) => {
  const Cmp = ICONS[icon] || Cloud;
  return <Cmp className={className} />;
};

const RADAR_LAYERS = [
  { key: 'radar', label: 'Radar' },
  { key: 'rain', label: 'Rain' },
  { key: 'wind', label: 'Wind' },
  { key: 'gust', label: 'Gusts' },
  { key: 'waves', label: 'Waves' },
  { key: 'temp', label: 'Temp' },
  { key: 'clouds', label: 'Clouds' },
];

const fmt = (v, digits = 0) =>
  (v === null || v === undefined || Number.isNaN(v)) ? '--' : Number(v).toFixed(digits);

const timeOnly = (iso) => {
  if (!iso) return '--';
  const [, t] = String(iso).split('T');
  if (!t) return '--';
  const [hStr, m] = t.split(':');
  let h = Number(hStr);
  const suffix = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m} ${suffix}`;
};

const dayName = (iso, index) => {
  if (index === 0) return 'Today';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { weekday: 'short' });
};

const dayDate = (iso) => {
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const compass = (deg) => {
  if (deg === null || deg === undefined) return '';
  const dirs = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  return dirs[Math.round(deg / 22.5) % 16];
};

const uvBand = (uv) => {
  if (uv === null || uv === undefined) return { label: '--', tone: 'bg-slate-200 text-slate-700' };
  if (uv < 3) return { label: 'Low', tone: 'bg-green-100 text-green-800' };
  if (uv < 6) return { label: 'Moderate', tone: 'bg-yellow-100 text-yellow-800' };
  if (uv < 8) return { label: 'High', tone: 'bg-orange-100 text-orange-800' };
  if (uv < 11) return { label: 'Very High', tone: 'bg-red-100 text-red-800' };
  return { label: 'Extreme', tone: 'bg-purple-100 text-purple-800' };
};

const StatTile = ({ icon: Icon, label, value, sub, testId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid={testId}>
    <div className="flex items-center gap-2 text-slate-500">
      <Icon className="w-4 h-4 text-blue-600" />
      <span className="text-xs font-bold uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
    {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

const WeatherPage = () => {
  const { weather_enabled, _loaded } = useSiteFeatureFlags();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [overlay, setOverlay] = useState('radar');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    setSeoMetadata({
      title: 'St John Weather | 7-Day Forecast, Radar & Climate Averages | St John USVI',
      description:
        'Live St John, USVI weather with a 7-day forecast, interactive radar, hourly outlook, sunrise and sunset times, plus seasonal temperature and monthly rainfall averages.',
      keywords:
        'st john weather, st john usvi forecast, virgin islands radar, st john rainfall averages, caribbean hurricane weather',
      canonicalPath: '/island-info/weather-in-st-john-vi',
      ogType: 'website',
    });
  }, []);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/api/public/weather`);
      if (!res.ok) throw new Error(`Request failed: ${res.status}`);
      setData(await res.json());
      setError('');
    } catch (e) {
      setError('Live weather is temporarily unavailable.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (data?.radar?.default_overlay) setOverlay(data.radar.default_overlay);
  }, [data?.radar?.default_overlay]);

  const windyUrl = useMemo(() => {
    const r = data?.radar;
    if (!r) return '';
    const p = new URLSearchParams({
      lat: r.latitude, lon: r.longitude,
      detailLat: r.latitude, detailLon: r.longitude,
      zoom: String(r.zoom || 8),
      level: 'surface',
      overlay,
      product: 'ecmwf',
      menu: '', message: 'true', marker: 'true',
      calendar: 'now', pressure: '', type: 'map',
      location: 'coordinates', detail: '',
      metricWind: 'mph', metricTemp: '°F',
      radarRange: '-1',
    });
    return `https://embed.windy.com/embed2.html?${p.toString()}`;
  }, [data?.radar, overlay]);

  const cur = data?.current;
  const units = data?.units || {};
  const climate = data?.climate;
  const uv = uvBand(cur?.uv_index);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 lg:pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4">
          <header className="text-center mb-10">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold italic uppercase text-slate-900 tracking-tight">
              Weather in {data?.location?.name || 'St John'}
            </h1>
            <p className="font-script text-3xl text-cyan-500 mt-3">
              {data?.location?.region || 'St John, Virgin Islands'}
            </p>
          </header>

          {loading || !_loaded ? (
            <div className="flex justify-center py-16" data-testid="weather-loading">
              <div className="w-10 h-10 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !weather_enabled ? (
            <p className="text-center text-slate-500 py-16" data-testid="weather-disabled">
              Our weather page is being updated. Please check back soon.
            </p>
          ) : error && !data ? (
            <p className="text-center text-slate-500 py-16" data-testid="weather-error">{error}</p>
          ) : (
            <>
              {/* ---------- current conditions ---------- */}
              <section
                className={`rounded-3xl overflow-hidden shadow-2xl ${
                  cur?.is_day
                    ? 'bg-gradient-to-br from-sky-500 via-blue-600 to-blue-800'
                    : 'bg-gradient-to-br from-slate-800 via-indigo-900 to-slate-900'
                }`}
                data-testid="weather-current"
              >
                <div className="p-8 lg:p-10 grid lg:grid-cols-[minmax(0,1fr)_auto] gap-8 items-center">
                  <div className="flex items-center gap-6">
                    <WeatherIcon icon={cur?.weather?.icon} className="w-24 h-24 text-white/95 shrink-0 drop-shadow-lg" />
                    <div className="min-w-0">
                      <div className="flex items-start gap-1">
                        <span className="text-6xl lg:text-7xl font-extrabold text-white leading-none" data-testid="weather-temp">
                          {fmt(cur?.temperature)}
                        </span>
                        <span className="text-2xl font-bold text-white/80 mt-2">{units.temperature}</span>
                      </div>
                      <p className="mt-2 text-xl font-bold text-white">{cur?.weather?.label}</p>
                      <p className="text-white/80 text-sm mt-1">
                        Feels like {fmt(cur?.feels_like)}{units.temperature} &middot; Updated {timeOnly(cur?.time)}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2 gap-4 lg:min-w-[280px]">
                    {[
                      { label: 'Wind', value: `${fmt(cur?.wind_speed)} ${units.wind_speed}`, sub: compass(cur?.wind_direction) },
                      { label: 'Humidity', value: `${fmt(cur?.humidity)}%` },
                      { label: 'Gusts', value: `${fmt(cur?.wind_gusts)} ${units.wind_speed}` },
                      { label: 'Clouds', value: `${fmt(cur?.cloud_cover)}%` },
                    ].map((s) => (
                      <div key={s.label} className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3">
                        <p className="text-[11px] font-bold uppercase tracking-wide text-white/70">{s.label}</p>
                        <p className="text-lg font-bold text-white">{s.value}</p>
                        {s.sub && <p className="text-xs text-white/70">{s.sub}</p>}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="px-8 lg:px-10 py-3 bg-black/20 flex flex-wrap items-center justify-between gap-3 text-white/85 text-sm">
                  <span className="inline-flex items-center gap-4">
                    <span className="inline-flex items-center gap-1"><Sunrise className="w-4 h-4" /> {timeOnly(cur?.sunrise)}</span>
                    <span className="inline-flex items-center gap-1"><Sunset className="w-4 h-4" /> {timeOnly(cur?.sunset)}</span>
                  </span>
                  <button
                    onClick={() => load(true)}
                    className="inline-flex items-center gap-2 font-bold hover:text-white transition-colors"
                    data-testid="weather-refresh"
                  >
                    <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
                  </button>
                </div>
              </section>

              {/* ---------- detail stats ---------- */}
              <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-6" data-testid="weather-stats">
                <StatTile icon={Thermometer} label="Feels Like" value={`${fmt(cur?.feels_like)}${units.temperature}`} testId="stat-feels" />
                <StatTile icon={Droplets} label="Humidity" value={`${fmt(cur?.humidity)}%`} testId="stat-humidity" />
                <StatTile icon={Wind} label="Wind" value={`${fmt(cur?.wind_speed)}`} sub={`${units.wind_speed} ${compass(cur?.wind_direction)}`} testId="stat-wind" />
                <StatTile icon={Gauge} label="Pressure" value={fmt(cur?.pressure)} sub={units.pressure} testId="stat-pressure" />
                <StatTile icon={Umbrella} label="Rain Now" value={`${fmt(cur?.precipitation, 2)}`} sub={units.precipitation} testId="stat-rain" />
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm" data-testid="stat-uv">
                  <div className="flex items-center gap-2 text-slate-500">
                    <Eye className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-bold uppercase tracking-wide">UV Index</span>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-slate-900">{fmt(cur?.uv_index, 1)}</p>
                  <span className={`inline-block mt-1 text-xs font-bold px-2 py-0.5 rounded ${uv.tone}`}>{uv.label}</span>
                </div>
              </section>

              {/* ---------- hourly ---------- */}
              {data?.hourly?.length > 0 && (
                <section className="mt-10" data-testid="weather-hourly">
                  <h2 className="text-2xl font-extrabold italic uppercase text-slate-900 tracking-tight mb-4">
                    Next 24 Hours
                  </h2>
                  <div className="flex gap-3 overflow-x-auto pb-3">
                    {data.hourly.map((h) => (
                      <div key={h.time}
                        className="shrink-0 w-24 bg-white border border-slate-200 rounded-xl p-3 text-center shadow-sm">
                        <p className="text-xs font-bold text-slate-500">{timeOnly(h.time)}</p>
                        <WeatherIcon icon={h.weather?.icon} className="w-7 h-7 mx-auto my-2 text-blue-600" />
                        <p className="text-lg font-bold text-slate-900">{fmt(h.temp)}&deg;</p>
                        <p className="text-xs text-cyan-700 font-semibold">{fmt(h.precip_probability)}%</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* ---------- 7 day ---------- */}
              <section className="mt-12" data-testid="weather-forecast">
                <h2 className="text-2xl font-extrabold italic uppercase text-slate-900 tracking-tight mb-4">
                  7-Day Forecast
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
                  {(data?.daily || []).map((d, i) => (
                    <div key={d.date}
                      className={`rounded-2xl p-4 text-center border shadow-sm ${
                        i === 0 ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200'
                      }`}
                      data-testid={`forecast-day-${i}`}>
                      <p className="font-bold text-slate-900">{dayName(d.date, i)}</p>
                      <p className="text-xs text-slate-500">{dayDate(d.date)}</p>
                      <WeatherIcon icon={d.weather?.icon} className="w-10 h-10 mx-auto my-3 text-blue-600" />
                      <p className="text-sm font-bold text-slate-900">
                        {fmt(d.temp_max)}&deg; <span className="text-slate-400 font-medium">/ {fmt(d.temp_min)}&deg;</span>
                      </p>
                      <p className="mt-2 text-xs text-slate-600 leading-tight">{d.weather?.label}</p>
                      <div className="mt-3 pt-3 border-t border-slate-200 space-y-1 text-xs">
                        <p className="text-cyan-700 font-semibold inline-flex items-center gap-1">
                          <Umbrella className="w-3 h-3" /> {fmt(d.precip_probability)}%
                        </p>
                        <p className="text-slate-600 inline-flex items-center gap-1">
                          <Wind className="w-3 h-3" /> {fmt(d.wind_max)} {units.wind_speed}
                        </p>
                        <p className="text-slate-600">UV {fmt(d.uv_index, 1)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* ---------- radar ---------- */}
              <section className="mt-12" data-testid="weather-radar">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                  <h2 className="text-2xl font-extrabold italic uppercase text-slate-900 tracking-tight">
                    Live Radar &amp; Maps
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    {RADAR_LAYERS.map((l) => (
                      <button key={l.key} onClick={() => setOverlay(l.key)}
                        className={`px-3 py-1.5 text-sm font-bold rounded transition-colors ${
                          overlay === l.key ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                        }`}
                        data-testid={`radar-layer-${l.key}`}>
                        {l.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-lg bg-slate-100">
                  <iframe
                    key={overlay}
                    src={windyUrl}
                    title={`${data?.location?.name || 'St John'} ${overlay} map`}
                    className="w-full"
                    style={{ height: 520, border: 0 }}
                    loading="lazy"
                    data-testid="radar-iframe"
                  />
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Interactive map by Windy. Forecast data from{' '}
                  <a href={data?.source?.url} target="_blank" rel="noopener noreferrer" className="text-blue-700 hover:underline">
                    {data?.source?.name}
                  </a>.
                </p>
              </section>

              {/* ---------- climate tables ---------- */}
              {climate && (
                <section className="mt-16 space-y-12" data-testid="weather-climate">
                  {/* seasonal */}
                  <div>
                    <h2 className="text-2xl font-extrabold italic uppercase text-slate-900 tracking-tight mb-4">
                      {climate.seasonal_temps.title}
                    </h2>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
                      <table className="w-full text-sm" data-testid="table-seasonal">
                        <thead className="bg-slate-100">
                          <tr>
                            <th className="text-left px-4 py-3 font-bold text-slate-900">Island / Season</th>
                            {climate.seasonal_temps.columns.map((c) => (
                              <th key={c.key} className="px-4 py-3 font-bold text-slate-900 text-center">
                                {c.label}
                                <span className="block text-xs font-normal text-slate-500">{c.sub}</span>
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {climate.seasonal_temps.rows.map((r) => {
                            const hot = r.island === climate.seasonal_temps.highlight_row;
                            return (
                              <tr key={r.island} className={hot ? 'bg-blue-50 font-semibold' : 'odd:bg-white even:bg-slate-50'}>
                                <td className="px-4 py-3 text-slate-900">{r.island}</td>
                                {climate.seasonal_temps.columns.map((c) => (
                                  <td key={c.key} className="px-4 py-3 text-center text-slate-700">{r[c.key]}&deg;</td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* monthly max/min */}
                  <div>
                    <h2 className="text-2xl font-extrabold italic uppercase text-slate-900 tracking-tight mb-4">
                      {climate.monthly_temps.title}
                    </h2>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
                      <table className="w-full text-sm" data-testid="table-monthly-temps">
                        <thead className="bg-slate-100">
                          <tr>
                            <th rowSpan={2} className="text-left px-4 py-2 font-bold text-slate-900 align-bottom">Month</th>
                            {climate.monthly_temps.islands.map((isl) => (
                              <th key={isl} colSpan={2} className="px-4 py-2 font-bold text-slate-900 text-center border-l border-slate-200">
                                {isl}
                              </th>
                            ))}
                          </tr>
                          <tr className="text-xs text-slate-500">
                            {climate.monthly_temps.islands.map((isl) => (
                              <React.Fragment key={isl}>
                                <th className="px-3 py-1 font-semibold border-l border-slate-200">Max</th>
                                <th className="px-3 py-1 font-semibold">Min</th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {climate.monthly_temps.rows.map((r) => (
                            <tr key={r.month} className="odd:bg-white even:bg-slate-50">
                              <td className="px-4 py-2.5 font-semibold text-slate-900">{r.month}</td>
                              {r.values.map((pair, idx) => (
                                <React.Fragment key={idx}>
                                  <td className={`px-3 py-2.5 text-center border-l border-slate-200 ${idx === 2 ? 'text-blue-800 font-semibold' : 'text-slate-700'}`}>{pair[0]}&deg;</td>
                                  <td className={`px-3 py-2.5 text-center ${idx === 2 ? 'text-blue-800 font-semibold' : 'text-slate-700'}`}>{pair[1]}&deg;</td>
                                </React.Fragment>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* monthly rainfall */}
                  <div>
                    <h2 className="text-2xl font-extrabold italic uppercase text-slate-900 tracking-tight mb-4 flex items-center gap-2">
                      <Waves className="w-6 h-6 text-blue-600" />
                      {climate.monthly_rainfall.title}
                    </h2>
                    <div className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm">
                      <table className="w-full text-sm" data-testid="table-rainfall">
                        <thead className="bg-slate-100">
                          <tr>
                            <th rowSpan={2} className="text-left px-4 py-2 font-bold text-slate-900 align-bottom">Month</th>
                            {climate.monthly_rainfall.islands.map((isl) => (
                              <th key={isl} colSpan={3} className="px-4 py-2 font-bold text-slate-900 text-center border-l border-slate-200">
                                {isl}
                              </th>
                            ))}
                          </tr>
                          <tr className="text-xs text-slate-500">
                            {climate.monthly_rainfall.islands.map((isl) => (
                              <React.Fragment key={isl}>
                                <th className="px-2 py-1 font-semibold border-l border-slate-200">Avg</th>
                                <th className="px-2 py-1 font-semibold">High</th>
                                <th className="px-2 py-1 font-semibold">Low</th>
                              </React.Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {climate.monthly_rainfall.rows.map((r) => (
                            <tr key={r.month} className="odd:bg-white even:bg-slate-50">
                              <td className="px-4 py-2.5 font-semibold text-slate-900">{r.month}</td>
                              {r.values.map((trio, idx) => (
                                <React.Fragment key={idx}>
                                  <td className={`px-2 py-2.5 text-center border-l border-slate-200 ${idx === 2 ? 'text-blue-800 font-semibold' : 'text-slate-700'}`}>{trio[0].toFixed(2)}</td>
                                  <td className={`px-2 py-2.5 text-center ${idx === 2 ? 'text-blue-800' : 'text-slate-600'}`}>{trio[1].toFixed(2)}</td>
                                  <td className={`px-2 py-2.5 text-center ${idx === 2 ? 'text-blue-800' : 'text-slate-600'}`}>{trio[2].toFixed(2)}</td>
                                </React.Fragment>
                              ))}
                            </tr>
                          ))}
                          <tr className="bg-blue-50 font-bold">
                            <td className="px-4 py-3 text-slate-900">Avg Year</td>
                            {climate.monthly_rainfall.year_totals.map((t, idx) => (
                              <td key={idx} colSpan={3} className="px-2 py-3 text-center text-blue-900 border-l border-slate-200">
                                {t.toFixed(2)}&quot;
                              </td>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </section>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default WeatherPage;
